import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import NumericTextBox from "./NumericTextBox";
import DropDownList from "./DropDownList";
import _ from 'lodash';
import "./UnitNumericTextBox.scss";

/**
 * Unit numeric text box component
 * @module
 * @param {string} name - Name
 * @param {func} onChange - Function invoked on value change
 * @param {number} [value] - Current value
 * @param {bool} [required] - Is input required?
 * @param {bool} [disabled] - Is input readonly?
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {number} [min] - Minimum number
 * @param {number} [max] - Maximum number
 * @param {number} [step] - Step
 * @param {(string | object)} [format] - Format
 * @param {bool} [autofocus] - Should input be focused?
 * @param {func} [onBlur] - Function invoked on input blur
 * @param {number} [tabIndex] - NumericTextBox Tabindex
 */

const UnitNumericTextBox = (props) => {
const { units, onChange } = props;
  const baseUnit = units ? units.find(u => u.isDefault) || units[0] : 
    {       
      key: 1,
      label: props.unit,
      isDefault: true,
      toBaseFormula: "x",
      fromBaseFormula: "x",
      format: props.format
    };

  const [selectedUnit, setSelectedUnit] = useState(units ? units.find(u => u.selected) : baseUnit);
  const [displayValue, setDisplayValue] = useState(props.value);

  const convert = useCallback((formula, x) => {
    try {
      const fn = new Function("x", `return ${formula};`);
      const res = fn(x);
      return Number.isFinite(res) ? res : null;
    } catch (err) {
      console.error("Conversion error:", formula, err);
      return x;
    }
  }, [units]);

  useEffect(() => {
    if (!_.isNumber(props.value)) {
      setDisplayValue(null);
      return;
    }    
    const converted = convert(selectedUnit?.fromBaseFormula, props.value);
    setDisplayValue(converted);
  }, [props.value, selectedUnit]);

  const handleUnitChange = (name, unit) => {
    if (!unit) return;
    setSelectedUnit(unit);
    if (_.isNumber(props.value)) {
      const converted = convert(unit.fromBaseFormula, props.value);      
      setDisplayValue(converted);
    }
  };

  const handleChange = (name, value) => {
    setDisplayValue(value);
    let baseValueToSend = value;
    if (units) {
      if (_.isNumber(Number(value))) {
        baseValueToSend = convert(selectedUnit.toBaseFormula, Number(value));        
      } else {
        baseValueToSend = null;
      }
    }
    onChange(name, baseValueToSend);
  };

  const element = (
    <div className={`form-input unit-numeric-input ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")} ${props.className ? props.className : ""}`}>
      <NumericTextBox
        data-testid={props.name || props.title}
        name={props.name}
        type={props.type}
        onChange={handleChange}
        value={displayValue}
        required={props.required}
        validationMessage={props.validationMessage}
        disabled={props.disabled}
        recommended={props.recommended}
        min={props.min}
        max={props.max}
        step={props.step}
        format={selectedUnit.format}
        tabIndex={props.tabIndex}
        autofocus={props.autofocus}
        onBlur={props.onBlur}
        spinners={props.spinners}
        validationAllowed={props.validationAllowed}
        title={props.title}
      />
      {units && <DropDownList
        className={`unit-select dropdownlist ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")} ${`unit-${props.name}` || ""}`}
        data={units}
        textField="label"
        dataItemKey={"key"}
        value={selectedUnit}
        onChange={handleUnitChange}
        required={props.required}
        disabled={props.disabled}
        name={`unit-${props.name}`}
      />}
      {!units && <div className="unit">
        <span>{props.unit}</span>
      </div>}
      {props.tooltip && <div className={"detail-tooltip"}>{props.tooltip}</div>}
    </div>
  );

  return props.notUi ? (
    element
  ) : (
    <div title={props.tooltip}
      className={`k-form-field ${props.required ? "required" : ""} ${props.disabled ? "disabled" : ""} form-unitnumeric ui-field form-field-${props.name}`}
    >
      <span className="detail-label">{props.title}</span>
      {element}
    </div>
  );
};

UnitNumericTextBox.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.number,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  validationMessage: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  format: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  autofocus: PropTypes.bool,
  onBlur: PropTypes.func,
  tabIndex: PropTypes.number,
  title: PropTypes.string,
  unit: PropTypes.string,
  spinners: PropTypes.bool,
  tooltip: PropTypes.string,
  notUi: PropTypes.bool,
};

export default UnitNumericTextBox;